import { createLogger } from "logging";

const logger = createLogger("helpers/lyneDates");

export type LyneDateValue = string | undefined | null;

function formatDate(d: string, m: string, y: string, allowPartial = false): string {
  const dd = allowPartial ? d.padStart(2, "0") : d;
  const mm = allowPartial ? m.padStart(2, "0") : m;
  let yyyy = allowPartial && y.length === 2 ? `20${y}` : y;
  yyyy = allowPartial && y.length === 1 ? `200${y}` : yyyy;

  if (!allowPartial) {
    if (dd.length !== 2) {
      logger.trace(`formatDate(): day '${dd}' is ambiguous (1 digit only)`);
      return "";
    }

    if (mm.length !== 2) {
      logger.trace(`formatDate(): month '${mm}' is ambiguous (1 digit only)`);
      return "";
    }

    if (yyyy.length !== 4) {
      logger.trace(`formatDate(): year '${yyyy}' is ambiguous (1 digit only)`);
      return "";
    }
  }

  const ddNum = parseInt(dd, 10);
  if (Number.isNaN(ddNum) || ddNum < 1 || ddNum > 31) {
    logger.trace(`formatDate(): day '${dd}' invalid (out of range)`);
    return "";
  }

  const mmNum = parseInt(mm, 10);
  if (Number.isNaN(mmNum) || mmNum < 1 || mmNum > 12) {
    logger.trace(`formatDate(): month '${mm}' invalid (out of range)`);
    return "";
  }

  const yyNum = parseInt(yyyy, 10);
  if (Number.isNaN(yyNum)) {
    logger.trace(`formatDate(): year '${yyyy}' invalid (out of range)`);
    return "";
  }

  const r = `${dd}.${mm}.${yyyy}`;
  logger.trace(`formatDate(): ${dd}.${mm}.${yyyy} -> ${r}`);

  return r;
}

function formatTime(h: string, m: string, allowPartial = false): string {
  const hh = allowPartial ? h.padStart(2, "0") : h;
  const mm = allowPartial ? m.padStart(2, "0") : m;

  if (!allowPartial) {
    if (h.length === 1) {
      logger.trace(`formatTime(): hour '${h}' is ambiguous (1 digit only)`);
      return "";
    }

    if (m.length === 1) {
      logger.trace(`formatTime(): minute '${m}' is ambiguous (1 digit only)`);
      return "";
    }
  }

  const hhNum = parseInt(hh, 10);
  if (Number.isNaN(hhNum) || hhNum < 0 || hhNum > 23) {
    logger.trace(`formatTime(): hour '${h}' invalid (out of range)`);
    return "";
  }

  const mmNum = parseInt(mm, 10);
  if (Number.isNaN(mmNum) || mmNum < 0 || mmNum > 59) {
    logger.trace(`formatTime(): minute '${m}' invalid (out of range)`);
    return "";
  }

  const r = `${hh}:${mm}`;
  logger.trace(`formatTime(): ${h}:${m} -> ${r}`);

  return r;
}

export function parseLyneDate(value: LyneDateValue, allowPartial = false): string {
  let r = value || "";
  r = r.includes(",") ? r.split(",")[1].trim() : r;

  // Accept dd.mm.yyyy with 1-2 digits for day/month and 4-digit year
  const match = r.match(/^(\d{1,2})\.(\d{1,2})\.(\d{1,4})$/);
  if (match) {
    const d = match[1].padStart(2, "0");
    const m = match[2].padStart(2, "0");
    const y = match[3];
    r = formatDate(d, m, y, allowPartial);
    if (r) {
      logger.trace(`parseLyneDate(): ${value} -> ${r}`);
      return r;
    }
  }

  // Allow single-digit year while finalizing input (on blur) when allowPartial=true.
  // E.g. '1.1.2' -> '01.01.2002'
  let numberMatch = r.match(/^(\d{2})(\d{2})(\d{2,4})$/);
  if (numberMatch) {
    const d = numberMatch[1];
    const m = numberMatch[2];
    const y = numberMatch[3];
    r = formatDate(d, m, y, allowPartial);
    if (r) {
      logger.trace(`parseLyneDate(): ${value} -> ${r}`);
      return r;
    }
  }

  logger.trace(`parseLyneDate(): ${value} -> invalid`);
  return "";
}

export function isValidTime(time: string, allowPartial = false): boolean {
  return allowPartial ? /^\d{1,2}:\d{1,2}$/.test(time) : /^\d{2}:\d{2}$/.test(time);
}

export function getHoursOrDefault(time: string, allowPartial = false): string | undefined {
  let result = undefined;
  if (!isValidTime(time, allowPartial)) {
    logger.trace(`getHoursOrDefault(): time:='${time}', allowPartial:=${allowPartial}, is invalid -> `, result);
    return result;
  }
  result = time.split(":")[0];
  logger.trace(`getHoursOrDefault(): time:='${time}', allowPartial:=${allowPartial}, is valid -> `, result);
  return result;
}

export function getMinutesOrDefault(time: string, allowPartial = false): string | undefined {
  let result = undefined;
  if (!isValidTime(time, allowPartial)) {
    logger.trace(`getMinutesOrDefault(): time:='${time}', allowPartial:=${allowPartial}, is invalid -> `, result);
    return result;
  }
  result = time.split(":")[1];
  logger.trace(`getMinutesOrDefault(): time:='${time}', allowPartial:=${allowPartial}, is valid -> `, result);
  return result;
}

export function getHoursAsNumber(
  time: string,
  allowPartial = false,
  defaultValue: number | undefined = undefined
): number | undefined {
  const hours = getHoursOrDefault(time, allowPartial);
  const result = hours !== undefined ? parseInt(hours, 10) : NaN;
  if (isNaN(result)) {
    logger.trace(`getHoursAsNumber(): invalid time '${time}' -> `, defaultValue);
    return defaultValue;
  }

  return result;
}

export function getMinutesAsNumber(
  time: string,
  allowPartial = false,
  defaultValue: number | undefined = undefined
): number | undefined {
  const minutes = getMinutesOrDefault(time, allowPartial);
  const result = minutes !== undefined ? parseInt(minutes, 10) : NaN;
  if (isNaN(result)) {
    logger.trace(`getMinutesAsNumber(): invalid time '${time}' -> `, defaultValue);
    return defaultValue;
  }

  return result;
}

export function parseLyneTime(value: LyneDateValue, allowPartial = false): string {
  let r = value || "";

  if (r.includes(":")) {
    const h = getHoursAsNumber(r, allowPartial);
    const hs = h !== undefined ? h.toString().padStart(2, "0") : "";
    const m = getMinutesAsNumber(r, allowPartial);
    const ms = m !== undefined ? m.toString().padStart(2, "0") : "";
    r = formatTime(hs, ms, allowPartial);
    logger.trace(`parseLyneTime(): ${value} -> ${r}`);
    if (r) {
      return r;
    }
  }

  // Handle 4-digit HHMM explicitly
  if (r.match(/^\d{4}$/)) {
    const hs = r.substring(0, 2);
    const ms = r.substring(2, 4);
    r = formatTime(hs, ms, allowPartial);
    logger.trace(`parseLyneTime(): ${value} -> ${r}`);
    if (r) {
      return r;
    }
  }

  // Handle 3-digit HMM explicitly. To avoid ambiguous auto-formatting while typing
  // (e.g. user typing "11:11" reaches "111" temporarily), we reject 3-digit
  // inputs that start with '1'. Accept other 3-digit inputs by padding to 4 digits.
  if (r.match(/^\d{3}$/) && allowPartial) {
    const padded = r.padStart(4, "0");
    const hs = padded.substring(0, 2);
    const ms = padded.substring(2, 4);
    r = formatTime(hs, ms, allowPartial);
    logger.trace(`parseLyneTime(): ${value} -> ${r}`);
    if (r) {
      return r;
    }
  }

  logger.trace(`parseLyneTime(): '${value}' -> invalid`);
  return "";
}

export function parseLyneDateTime(datePart: LyneDateValue, timePart: LyneDateValue, allowPartial = false): string {
  const date = parseLyneDate(datePart, allowPartial);
  const time = parseLyneTime(timePart, allowPartial);
  if (!date || !time) return "";
  const r = `${date} ${time}`;
  logger.trace(`parseLyneDateTime(): datePart=${datePart}, timePart=${timePart} -> ${r}`);
  return r;
}

export function setTimePart(date: Date | undefined, timePart: string): Date | undefined {
  if (!date) {
    throw new Error("setTimePart(): date is required");
  }

  const hours = getHoursAsNumber(timePart);
  if (hours === undefined) throw new Error(`setTimePart(): invalid time '${timePart}' (hours)`);

  const minutes = getMinutesAsNumber(timePart);
  if (minutes === undefined) throw new Error(`setTimePart(): invalid time '${timePart}' (minutes)`);

  date.setHours(hours, minutes, 0, 0);

  return date;
}
